'use client';

import { useId, useState } from 'react';
import { ArrowUpRight, ImageOff, Instagram } from 'lucide-react';
import { instagramPermalink, instagramPreviewUrl } from '@/lib/instagram-media-url';

/** Public API projection: publication consent and channel eligibility are server-owned. */
export interface InstagramGalleryItem {
  id: string;
  caption: string | null;
  mediaType: string;
  /** For VIDEO this must be a still thumbnail, never the provider's video URL. */
  previewUrl: string | null;
  permalink: string | null;
  postedAt: string;
  username: string;
}

function InstagramPost({ item, compact }: { item: InstagramGalleryItem; compact: boolean }) {
  const previewUrl = instagramPreviewUrl(item.previewUrl);
  const permalink = instagramPermalink(item.permalink);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const author = item.username ? `@${item.username.replace(/^@/, '')}` : 'Instagram';
  const postedAt = new Date(item.postedAt);
  const hasDate = !Number.isNaN(postedAt.getTime());
  return (
    <li className="overflow-hidden rounded-xl border bg-card text-card-foreground">
      <figure className="flex h-full flex-col">
        <div className="relative aspect-square overflow-hidden bg-muted">
          {previewUrl && failedUrl !== previewUrl ? (
            // Provider thumbnails expire; render directly with a source-link fallback.
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={previewUrl}
              alt={`Instagram ${item.mediaType === 'VIDEO' ? 'video thumbnail' : 'photo'} by ${author}`}
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              onError={() => setFailedUrl(previewUrl)}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-sm text-muted-foreground">
              <ImageOff className="h-6 w-6" aria-hidden="true" />
              <span>Preview unavailable</span>
            </div>
          )}
          {item.mediaType === 'VIDEO' && (
            <span className="absolute left-2 top-2 rounded bg-background/95 px-2 py-1 text-xs">Video thumbnail</span>
          )}
        </div>
        <figcaption className="flex flex-1 flex-col gap-2 p-3">
          <p className="break-words text-sm font-medium">{author}</p>
          {item.caption && (
            <p className={`${compact ? 'line-clamp-2' : 'line-clamp-3'} whitespace-pre-line break-words text-sm text-muted-foreground`}>
              {item.caption}
            </p>
          )}
          {hasDate && (
            <time dateTime={postedAt.toISOString()} className="text-xs text-muted-foreground">
              {postedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
            </time>
          )}
          {permalink ? (
            <a
              href={permalink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View original Instagram post by ${author} (opens in a new tab)`}
              className="mt-auto inline-flex min-h-11 items-center gap-1 rounded text-sm font-medium underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              View on Instagram <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
          ) : (
            <p className="mt-auto text-xs text-muted-foreground">Original post unavailable</p>
          )}
        </figcaption>
      </figure>
    </li>
  );
}

export function InstagramGallery({ items, compact = false }: { items: InstagramGalleryItem[]; compact?: boolean }) {
  const headingId = useId();
  if (!items.length) return null;
  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <h2 id={headingId} className="flex items-center gap-2 text-lg font-semibold">
        <Instagram className="h-5 w-5" aria-hidden="true" /> From Instagram
      </h2>
      <ul className={`grid grid-cols-2 gap-3 ${compact ? 'sm:grid-cols-3' : 'md:grid-cols-3 lg:grid-cols-4'}`}>
        {items.map((item) => <InstagramPost key={item.id} item={item} compact={compact} />)}
      </ul>
    </section>
  );
}
